import { AnimatePresence } from "motion/react";
import { memo, useMemo } from "react";

import type { AdminTicketActivityDto, AdminTicketCommentDto, AdminTicketDetailsDto, AppTicketDto } from "@/api/types";
import { formatDate, FormattedDate } from "@/components/FormattedDate/FormattedDate";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Badge } from "@/design-system/Badge";
import { Inline } from "@/design-system/Inline";
import { RatingBadge } from "@/design-system/RatingBadge";
import { isDefined } from "@/helpers/util";
import {
  ActivityDetailLabel,
  ActivityDetailRemark,
  ActivityDetails,
  ActivityDetailValue,
} from "@/product-patterns/tickets/ActivityDetails";
import {
  ActivityStatusValue,
  ActivityTimeline,
  ActivityTimelineGroup,
  ActivityTranslatedValue,
} from "@/product-patterns/tickets/ActivityTimeline";
import { Trans, useTranslation } from "@/translations";

import { groupActivities, TicketActivityWrapper, TicketComment } from "./TicketActivity";

interface TicketActivity extends AdminTicketActivityDto {
  createdAt?: string;
}
interface TicketActivitySectionProps {
  ticket: AdminTicketDetailsDto | AppTicketDto;
  daysOpen?: number;
  activities: TicketActivity[];
  onNoteEdit?: (comment: AdminTicketCommentDto) => void;
  isDescending?: boolean;
  isForResident?: boolean;
  scrollTarget?: React.ReactNode;
}
function TicketActivitySectionInternal({
  ticket,
  daysOpen,
  onNoteEdit,
  activities,
  isDescending,
  isForResident,
  scrollTarget,
}: TicketActivitySectionProps): React.ReactNode {
  const { t } = useTranslation();
  const groupedActivities = useMemo(() => groupActivities(activities), [activities]);

  if (activities.length === 0) {
    return <span>{t("page.tickets.details.activities.none")}</span>;
  }

  return (
    <ActivityTimeline>
      {isDescending && scrollTarget}
      {groupedActivities.map(([date, activities]) => (
        <ActivityTimelineGroup
          key={date.toString()}
          data-testid="activities-date-group"
          date={<FormattedDate format="dateRelative" date={new Date(Number(date)).toISOString()} />}
        >
          <AnimatePresence>
            {activities.map((activity) => {
              if (activity.comment) {
                return (
                  <TicketComment
                    key={"comment-" + activity.comment.id}
                    ticketId={ticket.id}
                    comment={activity.comment}
                    onEdit={onNoteEdit}
                  />
                );
              }
              if (activity.assigneeChange) {
                return (
                  <TicketAssigneeChangeActivityItem
                    key={activity.id}
                    {...{
                      activity,
                    }}
                  />
                );
              }
              if (activity.statusChange) {
                return (
                  <TicketStatusChangeActivityItem
                    key={activity.id}
                    {...{
                      activity,
                    }}
                  />
                );
              }
              if (activity.createdAt) {
                return (
                  <TicketCreationActivityItem
                    key={activity.id}
                    {...{
                      activity,
                    }}
                  />
                );
              }
              if (activity.rating) {
                return (
                  <TicketRatingActivityItem
                    key={activity.id}
                    isForResident={isForResident}
                    amountDaysOpen={daysOpen}
                    {...{
                      activity,
                    }}
                  />
                );
              }
            })}
          </AnimatePresence>
        </ActivityTimelineGroup>
      ))}
      {!isDescending && scrollTarget}
    </ActivityTimeline>
  );
}
type TicketActivityItemBaseProp = {
  activity: TicketActivity;
};
const TicketAssigneeChangeActivityItem = ({ activity }: TicketActivityItemBaseProp) => {
  if (!activity.assigneeChange) {
    return null;
  }

  return (
    <TicketActivityWrapper key={activity.id} author={activity.author} postedAt={activity.postedAt}>
      {activity.assigneeChange.oldAssignee ? (
        <Trans
          i18nKey="page.tickets.details.activities.assignee-change"
          components={{
            assignee: (
              <ActivityTranslatedValue
                data-testid="activity-assignee-change"
                data-value={activity.assigneeChange.newAssignee.fullName}
              />
            ),
          }}
          values={{
            oldAssignee: activity.assigneeChange.oldAssignee.fullName,
            newAssignee: activity.assigneeChange.newAssignee.fullName,
          }}
        />
      ) : (
        <Trans
          i18nKey="page.tickets.details.activities.new-assignee"
          components={{
            assignee: (
              <ActivityTranslatedValue
                data-testid="activity-assignee-change"
                data-value={activity.assigneeChange.newAssignee.fullName}
              />
            ),
          }}
          values={{
            newAssignee: activity.assigneeChange.newAssignee.fullName,
          }}
        />
      )}
    </TicketActivityWrapper>
  );
};
const TicketStatusChangeActivityItem = ({ activity }: TicketActivityItemBaseProp) => {
  if (!activity.statusChange) {
    return null;
  }

  return (
    <TicketActivityWrapper key={activity.id} author={activity.author} postedAt={activity.postedAt}>
      <Trans
        i18nKey="page.tickets.details.activities.status-change"
        components={{
          oldStatus: (
            <ActivityStatusValue>
              <TicketStatus
                description={activity.statusChange.oldStatus.name}
                labelColor={activity.statusChange.oldStatus.color}
                variant="dot"
              />
              <span />
            </ActivityStatusValue>
          ),
          newStatus: (
            <ActivityStatusValue data-testid="activity-status-change" data-value={activity.statusChange.newStatus.id}>
              <TicketStatus
                description={activity.statusChange.newStatus.name}
                labelColor={activity.statusChange.newStatus.color}
                variant="dot"
              />
              <span />
            </ActivityStatusValue>
          ),
        }}
      />
    </TicketActivityWrapper>
  );
};
const TicketCreationActivityItem = ({ activity }: TicketActivityItemBaseProp) => {
  const { t, i18n } = useTranslation();
  if (!activity.createdAt) {
    return null;
  }

  return (
    <TicketActivityWrapper key={activity.id} author={activity.author} postedAt={activity.postedAt}>
      {t("page.tickets.details.activities.created", {
        date: formatDate(i18n, "datetimeRelative", activity.postedAt),
      })}
    </TicketActivityWrapper>
  );
};
type TicketRatingActivityItemProps = TicketActivityItemBaseProp & {
  isForResident?: boolean;
  amountDaysOpen: number | undefined;
};
const TicketRatingActivityItem = ({ activity, isForResident, amountDaysOpen }: TicketRatingActivityItemProps) => {
  const { t } = useTranslation();

  if (!activity.rating) {
    return null;
  }

  const feedbackLabels = activity.rating.ratingFeedback?.map((item) => item.value) || [];
  const isRatingAvailable = isDefined(activity.rating.rating) && activity.rating.rating > 0;

  return (
    <TicketActivityWrapper key={activity.id} author={activity.author} postedAt={activity.postedAt}>
      <ActivityDetails>
        <ActivityDetailLabel>{t("page.tickets.details.activities.rated.rating")}</ActivityDetailLabel>
        {isRatingAvailable ? (
          <ActivityDetailValue data-testid="log-rating-value-element">
            <RatingBadge data-testid="log-rating-stars-element" variant="star" rating={activity.rating.rating!} />
          </ActivityDetailValue>
        ) : (
          <ActivityDetailValue data-testid="log-rating-value-element">
            {t("page.tickets.details.activities.rated.rating.not-relevant")}
          </ActivityDetailValue>
        )}

        {!isForResident && (
          <>
            <ActivityDetailLabel>{t("page.tickets.details.activities.rated.run-time")}</ActivityDetailLabel>
            <ActivityDetailValue data-testid="log-rating-runtime-element">
              {amountDaysOpen == null
                ? "-"
                : t("page.tickets.details.activities.rated.run-time.days", {
                    count: amountDaysOpen,
                  })}
            </ActivityDetailValue>
          </>
        )}

        {isRatingAvailable && (
          <>
            <ActivityDetailLabel>{t("page.tickets.details.activities.rated.feedback-topics")}</ActivityDetailLabel>
            <Inline gap="sm">
              {feedbackLabels.length === 0 && (
                <ActivityDetailValue>{t("common.label.not-applicable")}</ActivityDetailValue>
              )}
              {feedbackLabels.length > 0 &&
                feedbackLabels.map((label) => {
                  return (
                    <Badge data-testid="log-rating-label-element" size="md" key={label}>
                      {label}
                    </Badge>
                  );
                })}
            </Inline>
          </>
        )}

        <ActivityDetailLabel>{t("page.tickets.details.activities.rated.remark")}</ActivityDetailLabel>
        <ActivityDetailRemark data-testid="log-rating-remark-element">
          {activity.rating.ratingRemark || t("page.tickets.details.activities.rated.remark.none")}
        </ActivityDetailRemark>
      </ActivityDetails>
    </TicketActivityWrapper>
  );
};

export const TicketActivitySection = memo(TicketActivitySectionInternal);
